import { Box } from "@mui/material";
import React, { useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { customerColors } from "features/configure/chartColors";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import AutoCompleteDropdown, {
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import { useIntl } from "react-intl";
import _ from "lodash";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";

// 註解一下
// Bar Chart Component
// props: data - array - required - the data to be displayed in the circle chart.

export default function BehaviorBarChart({
  data,
  chartName,
  filters = [],
  otherDatas,
  type = "percent", // type 先分成純數字與百分比兩種，後續再增加更多種類，最底下再多改他要顯示的樣式
  loading = false,
  showSearchBar,
  searchText,
  setSearchText,
  tooltip,
}) {
  const { formatMessage } = useIntl();
  const hasAxisIcon = data.some((item) => item.icon);

  const colors = customerColors.middle;
  const gradientColors = customerColors.gradient;

  const cleanName = (name) =>
    (name || "").toString().replace(/[^a-zA-Z0-9]/g, "_"); // 替換空格為下劃線

  const richStyles = data.reduce((acc, item) => {
    acc[cleanName(item.name)] = {
      height: 20,
      width: 20,
      align: "center",
      backgroundColor: {
        image: item.icon,
      },
    };
    return acc;
  }, {});

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: function (params) {
        const { name, value, dataIndex } = params[0];
        if (name === "Others") {
          const othersDetails = otherDatas
            .map(
              (item) =>
                `<div style="display: flex; align-items: center; ">
                    <div style="
                      width: 16px; 
                      height: 8px; 
                      background-color: ${colors[dataIndex]};
                      margin-right: 8px;
                      border-radius: 2px;
                    "></div>
                    <div style="display: flex; align-items: center;justify-content: space-between;width:100%">
                      <p style="margin-right: 40px;">${item.name}</p>
                      <p>${item.value}${type == "number" ? "" : "%"}</p>
                    </div>
                  </div>`
            )
            .join(""); // 拼接成一个完整的 HTML 字符串

          return `
          <div style="font-size: 14px; color: #555;display: table;">
            ${othersDetails}
          </div>
        `;
        }
        return `
        <div style="display: flex; align-items: center; font-size: 14px; color: #555; padding: 2px;">
          <div style="
            width: 16px; 
            height: 8px; 
            background-color: ${colors[dataIndex]};
            margin-right: 8px;
            border-radius: 2px;
          "></div>
          <p style="margin-right: 40px;">${name}</p>
          <p>${value}${type == "number" ? "" : "%"}</p>
        </div>
      `;
      },
    },
    grid: {
      left: "0",
      right: "24px",
      top: "20px",
      bottom: "0",
      containLabel: true,
    },
    xAxis: {
      show: true,
      type: "category",
      data: data.map((item) => item.name),
      axisLabel: {
        formatter: function (value) {
          if (hasAxisIcon) return `{${cleanName(value)}|}\n${value}`;
          return value;
        },
        rich: richStyles,
        textStyle: {
          fontSize: 10,
          color: "#777",
          fontWeight: 300,
          lineHeight: 20,
        },
        interval: 0,
        overflow: "truncate",
        width: 80,
      },
    },
    yAxis: {
      show: true,
      type: "value",
      axisLabel: {
        formatter: (value) => {
          if (type === "percent") return value + "%";
          return value;
        },
        color: "#777",
        fontSize: 10,
        fontWeight: 300,
      },
    },
    series: [
      {
        data: data.map((item, index) => ({
          value: item.value,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(
              0,
              0,
              0,
              1, // 渐变方向：从上到下
              [
                { offset: 0, color: gradientColors[index][1] }, // 顶部颜色
                { offset: 1, color: gradientColors[index][0] }, // 底部颜色
              ]
            ),
            borderRadius: [4, 4, 0, 0],
          },
        })),
        barWidth: 40,
        type: "bar",
        label: {
          show: true,
          position: "top",
          formatter: (params) =>
            `${params.value}${type == "number" ? "" : "%"}`,
          fontSize: 10,
          fontWeight: 300,
          color: "#777",
        },
      },
    ],
  };

  return (
    <Box
      sx={{
        width: "100%", // Adjust the width as needed
        backgroundColor: "#FFF",
        borderRadius: "4px",
        p: "20px 16px 8px",
      }}
    >
      <Box className="item-flex" justifyContent="space-between" mb="16px">
        <Box fontSize="16px" color="#555">
          {chartName} {tooltip && <IconTooltip content={tooltip} />}
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {showSearchBar && (
            <InputField
              size={SIZE.MEDIUM}
              style={{ width: "200px" }}
              startAdornment={
                <i className="meta-crm-icon-ic_search font-size-20" />
              }
              placeholder={formatMessage({
                id: "global.demographic.searchNft",
              })}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          )}
          {filters.map((filter, index) => (
            <Box sx={{ width: "156px" }}>
              <AutoCompleteDropdown
                key={index}
                options={filter.options}
                value={filter.selectedOption}
                placeholder={formatMessage({ id: "menu.placeholder.select" })}
                onChange={(e, v) => filter.setOption(v)}
                size={SIZE.MEDIUM}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {loading ? (
        <LoadingIcon isAbsolute />
      ) : data.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            height: "261px",
          }}
        >
          <Box
            sx={{
              height: "242px",
              width: "100%",
              flexShrink: 1,
            }}
          >
            <ReactECharts
              option={option}
              autoResize={true}
              style={{ height: "100%", width: "100%" }}
              notMerge={true}
            />
          </Box>
          {/* <Box
            flexShrink={0}
            mx="24px"
            my="auto"
            height="200px"
            width="1px"
            bgcolor="#E6E6E6"
          />
          <Box
            sx={{
              flexShrink: 0,
              width: "242px",
              color: "#777",
            }}
          >
            <Box>
              {data?.slice(0, 10).map((item, index) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: "8px",
                  }}
                  key={index}
                >
                  <Box
                    sx={{
                      height: "8px",
                      width: "13px",
                      borderRadius: "2px",
                      backgroundColor: colors[index],
                      mb: "1px",
                      mr: "8px",
                    }}
                  />
                  <Box
                    key={index}
                    sx={{
                      color: "#777",
                      fontWeight: 400,
                      fontSize: "14px",
                    }}
                  >
                    {item.name}：
                    {type === "number"
                      ? parseInt(item.value).toLocaleString()
                      : item.value + "%"}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box> */}
        </Box>
      ) : (
        <EmptyIconStatus
          sx={{ width: "100%", margin: "81px 0" }}
          wording="later"
        />
      )}
    </Box>
  );
}
